import { captureEvent, identifyAnalyticsUser } from './analytics';
import { getTelemetryIdentity } from './telemetryIdentity';

type AuthEventType = 'login' | 'signup' | 'page_visit';
type UserStatus = 'anonymous' | 'free' | 'premium';


interface TrackPageVisitOptions {
  email: string | null;
  source?: string;
  accessToken?: string | null;
  isPremium?: boolean;
  pagePath: string;
}

interface TrackAuthSuccessOptions {
  eventType: Exclude<AuthEventType, 'page_visit'>;
  email: string | null;
  method: string;
  source: string;
  accessToken?: string | null;
  isPremium?: boolean;
}

const normalizeEmail = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim().toLowerCase();
  return trimmed.includes('@') ? trimmed : null;
};

const normalizeToken = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.replace(/^Bearer\s+/i, '') || null;
};

const resolveUserStatus = (email: string | null, isPremium = false): UserStatus => {
  if (!email) {
    return 'anonymous';
  }

  return isPremium ? 'premium' : 'free';
};

const notifyAuthEventRelay = async (
  eventType: AuthEventType,
  email: string,
  method: string,
  source: string,
  accessToken?: string | null,
  pagePath?: string,
  userStatus: UserStatus = 'anonymous',
  isPremium = false
): Promise<void> => {
  const token = normalizeToken(accessToken);
  const identity = getTelemetryIdentity();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch('/api/telemetry/auth-event', {
    method: 'POST',
    headers,
    keepalive: true,
    body: JSON.stringify({
      eventType,
      email,
      method,
      source,
      userAgent: navigator.userAgent || null,
      pagePath: pagePath || null,
      anonId: identity.anonId,
      sessionId: identity.sessionId,
      site: identity.site,
      userStatus,
      isPremium,
    }),
  });

  if (!response.ok) {
    throw new Error(`Auth event relay failed with ${response.status}`);
  }
};

export const trackPageVisitEvent = (options: TrackPageVisitOptions): void => {
  const normalizedEmail = normalizeEmail(options.email);
  const userStatus = resolveUserStatus(normalizedEmail, Boolean(options.isPremium));

  void notifyAuthEventRelay(
    'page_visit',
    normalizedEmail || 'anonymous@visitor.local',
    'navigation',
    options.source || 'app_route_change',
    options.accessToken,
    options.pagePath,
    userStatus,
    Boolean(options.isPremium)
  ).catch((error) => {
    console.warn('Failed to send page visit relay event:', error);
  });
};

export const trackAuthSuccess = (options: TrackAuthSuccessOptions): void => {
  const email = normalizeEmail(options.email);
  if (!email) {
    return;
  }

  const isPremium = Boolean(options.isPremium);
  const userStatus = resolveUserStatus(email, isPremium);
  identifyAnalyticsUser(email, {
    is_premium: isPremium,
    user_status: userStatus,
  });

  const eventName = options.eventType === 'signup' ? 'signup_success' : 'login_success';
  captureEvent(eventName, {
    auth_method: options.method,
    auth_source: options.source,
    user_status: userStatus,
    is_premium: isPremium,
  });

  void notifyAuthEventRelay(
    options.eventType,
    email,
    options.method,
    options.source,
    options.accessToken,
    undefined,
    userStatus,
    isPremium
  ).catch((error) => {
    console.warn('Failed to send auth relay event:', error);
  });
};
